import { useRef } from "react";

/**
 * Auction sound effects synthesized via Web Audio API — no external files.
 * - Countdown beeps at seconds 5..1
 * - Final higher beep at 0
 * - Fireworks burst on assignment
 */
export function useAuctionSounds() {
  const ctxRef = useRef(null);
  const lastCountdownRef = useRef(null);

  const ensureCtx = () => {
    if (typeof window === "undefined") return null;
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
    }
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
      const resume = () => {
        ctx.resume().catch(() => {});
        document.removeEventListener("pointerdown", resume);
        document.removeEventListener("keydown", resume);
      };
      document.addEventListener("pointerdown", resume, { once: true });
      document.addEventListener("keydown", resume, { once: true });
    }
    return ctx;
  };

  const beep = (freq = 800, dur = 0.15, gain = 0.2, type = "sine") => {
    const ctx = ensureCtx();
    if (!ctx || ctx.state !== "running") return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = type;
    o.frequency.value = freq;
    const t = ctx.currentTime;
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g); g.connect(ctx.destination);
    o.start(t); o.stop(t + dur + 0.02);
  };

  const playCountdown = (secondsRemaining) => {
    if (lastCountdownRef.current === secondsRemaining) return;
    lastCountdownRef.current = secondsRemaining;
    if (secondsRemaining > 5 || secondsRemaining < 1) return;
    if (secondsRemaining === 1) {
      // final tick — a longer higher tone
      beep(1200, 0.35, 0.28, "square");
    } else {
      beep(880, 0.13, 0.2, "sine");
    }
  };

  const resetCountdown = () => { lastCountdownRef.current = null; };

  const fireworks = () => {
    const ctx = ensureCtx();
    if (!ctx || ctx.state !== "running") return;
    const bursts = [0, 0.35, 0.75, 1.1, 1.5];
    bursts.forEach((delay) => {
      const now = ctx.currentTime + delay;
      // Low boom
      const boom = ctx.createOscillator();
      const boomG = ctx.createGain();
      boom.type = "sine";
      boom.frequency.setValueAtTime(140, now);
      boom.frequency.exponentialRampToValueAtTime(40, now + 0.25);
      boomG.gain.setValueAtTime(0.35, now);
      boomG.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      boom.connect(boomG); boomG.connect(ctx.destination);
      boom.start(now); boom.stop(now + 0.32);
      // Crackle: short white noise burst with decay
      const size = Math.floor(ctx.sampleRate * 0.45);
      const buf = ctx.createBuffer(1, size, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < size; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / size);
      }
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      const ng = ctx.createGain();
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass"; hp.frequency.value = 800;
      ng.gain.setValueAtTime(0.22, now + 0.05);
      ng.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      noise.connect(hp); hp.connect(ng); ng.connect(ctx.destination);
      noise.start(now + 0.05); noise.stop(now + 0.5);
    });
  };

  return { playCountdown, resetCountdown, fireworks, ensureCtx };
}
